<template>
  <div class="floating-dropdown">
    <button
      ref="toggle"
      class="floating-dropdown__toggle"
      @click="toggle"
      :class="[
        this.open ? 'floating-dropdown__toggle--open' : '',
      ]"
    >
      <slot name="toggle"/>
    </button>

    <transition name="floating-dropdown__dropdown-">
      <div
        v-if="open"
        :style="{right: `${rightOffset}px`}"
        class="floating-dropdown__dropdown"
        v-click-away="away"
      >
        <slot/>
      </div>
    </transition>
  </div>
</template>

<script>
  import { directive as vClickAway } from 'vue3-click-away';

  export default {
    directives: {
      clickAway: vClickAway,
    },
    props: {
      open: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        rightOffset: 0,
      }
    },
    watch: {
      open() {
        this.updateRightOffset();
      },
    },
    methods: {
      toggle() {
        if (this.open) {
          this.$emit('close');
        } else {
          this.updateRightOffset();
          this.$emit('open');
        }
      },
      away(event) {
        if (!this.open) {
          return;
        }

        // Toggle sits outside the dropdown panel. On touch devices
        // vue3-click-away listens to touchstart, which would close here
        // and then the button's click would reopen. Let toggle() handle it.
        const toggle = this.$refs.toggle;
        if (toggle && (toggle === event.target || toggle.contains(event.target))) {
          return;
        }

        // Click on a node removed during the same event (e.g. trash icon
        // removing a FavouriteCard) looks like an outside click because
        // vue3-click-away's contains() check fails on detached nodes.
        if (event.target instanceof Node && !event.target.isConnected) {
          return;
        }

        this.$emit('close');
      },
      updateRightOffset() {
        const screeWidth = Math.max(
          document.documentElement.clientWidth,
          window.innerWidth || 0
        );
        const dropdownWidth = 352;
        const margin = 8;

        if (screeWidth < dropdownWidth + margin * 2) {
          return margin;
        }

        const toggle = this.$refs.toggle;
        const toggleCenterFromRight = screeWidth - toggle.offsetLeft - toggle.offsetWidth / 2;

        this.rightOffset = Math.max(toggleCenterFromRight - dropdownWidth / 2, margin);
      },
    },
    mounted() {
      this.updateRightOffset();
      window.addEventListener('resize', this.updateRightOffset);
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.updateRightOffset);
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .floating-dropdown {
    position: relative;
  }

  .floating-dropdown__dropdown {
    position: absolute;
    overflow: hidden;
    width: r(352);
    max-width: calc(100vw - #{r(16)});
    background-color: $color-white;
    box-shadow: 0 r(2) r(4) 0 rgba(0, 0, 0, 0.5);
    border-radius: r(2);
    transition: opacity 0.2s, transform 0.2s;
    right: 0 !important;

    &--enter,
    &--leave-to {
      opacity: 0;
      transform: translateY(r(-8));
    }
  }

  .floating-dropdown__toggle {
    @include reset-button(true);

    &--open {
      background-color: $white !important;
    }
  }
</style>
